/**
 * Terminal Panel Component
 */

import { serial } from '../serial/serial.js';
import { notifications } from './notifications.js';
import { t } from '../utils/i18n.js';

export class Terminal {
    constructor() {
        this._output = document.getElementById('terminal-output');
        this._input = document.getElementById('terminal-input');
        this._history = [];
        this._historyIndex = -1;
        this._maxLines = 500;

        this._bindEvents();
        this._bindSerialEvents();
    }

    _bindEvents() {
        // Send button
        document.getElementById('btn-terminal-send')?.addEventListener('click', () => {
            this._sendCommand();
        });

        // Clear button
        document.getElementById('btn-terminal-clear')?.addEventListener('click', () => {
            this.clear();
        });

        // Input handling
        this._input?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                this._sendCommand();
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                this._navigateHistory(-1);
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                this._navigateHistory(1);
            }
        });
    }

    _bindSerialEvents() {
        // Log commands
        serial.on('command', (cmd) => {
            this._appendLine(cmd, 'cmd');
        });

        // Log responses
        serial.on('data', (data) => {
            this._appendLine(data, 'response');
        });

        // Log errors
        serial.on('error', (error) => {
            this._appendLine(`Error: ${error.message}`, 'error');
        });

        // Connection events
        serial.on('connected', (info) => {
            this._appendLine(`Connected: ${info || 'OK'}`, 'info');
        });

        serial.on('disconnected', () => {
            this._appendLine('Disconnected', 'info');
        });
    }

    async _sendCommand() {
        const cmd = this._input.value.trim();
        if (!cmd) return;

        // Add to history
        this._history.push(cmd);
        if (this._history.length > 100) {
            this._history.shift();
        }
        this._historyIndex = this._history.length;

        // Clear input
        this._input.value = '';

        if (!serial.connected) {
            this._appendLine('Not connected', 'error');
            return;
        }

        try {
            // The command already includes ~ prefix in UI prompt
            // But we need to handle it properly
            let cmdToSend = cmd;
            if (!cmd.startsWith('~')) {
                cmdToSend = cmd;
            } else {
                cmdToSend = cmd.substring(1); // Remove ~ as sendCommand adds it
            }

            const command = cmdToSend.charAt(0);
            const params = cmdToSend.substring(1);
            let success = true;

            // These payloads have protocol-specific packet limits. Route them
            // through the same validation/chunking used by the dedicated UI.
            if (command === 'X') {
                success = await serial.executeScript(params);
            } else if (command === 'W') {
                success = await serial.sendWS2812(params);
            } else {
                await serial.sendCommand(command, params);
            }

            if (!success) {
                this._appendLine('Command rejected by device', 'error');
            }
        } catch (error) {
            if (error.message === 'Timeout') {
                this._appendLine('Timeout', 'error');
            } else {
                this._appendLine(`Error: ${error.message}`, 'error');
            }
        }
    }

    _navigateHistory(direction) {
        if (this._history.length === 0) return;

        this._historyIndex += direction;

        if (this._historyIndex < 0) {
            this._historyIndex = 0;
        } else if (this._historyIndex >= this._history.length) {
            this._historyIndex = this._history.length;
            this._input.value = '';
            return;
        }

        this._input.value = this._history[this._historyIndex];
    }

    _appendLine(text, className = '') {
        const line = document.createElement('div');
        line.className = className;
        line.textContent = text;
        this._output.appendChild(line);

        // Limit lines
        while (this._output.children.length > this._maxLines) {
            this._output.removeChild(this._output.firstChild);
        }

        // Scroll to bottom
        this._output.scrollTop = this._output.scrollHeight;
    }

    clear() {
        this._output.innerHTML = '';
    }

    log(message, type = 'info') {
        this._appendLine(message, type);
    }
}
